import { Box, Button, Grid, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { FormProvider, useForm } from 'react-hook-form';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { toast } from 'react-toastify';
import { getAllOrganizations, updateorganization } from 'src/shared/services/organizationService';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ControlledDatePicker from 'src/shared/components/form/controlledDatePicker';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { OrganizationRequest } from '../models/organizationRequest';

export const OrganizationDetails = () => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState<boolean>(false);

  const requiredMessage = t('common:validationMessages.required');

  const OrganizationRequestSchema: Schema<OrganizationRequest> = yup.object().shape({
    countryAss_id: yup.number().required(),
    countryAssNr: yup.string().required(requiredMessage),
    countryAss: yup.string().required(requiredMessage),
    cOaddress: yup.string().optional().nullable(),
    address: yup.string().optional().nullable(),
    postNr: yup.string().optional().nullable(),
    postAddress: yup.string().optional().nullable(),
    addressLocal: yup.string().optional().nullable(),
    phone: yup.string().optional().nullable(),
    phoneOther: yup.string().optional().nullable(),
    fax: yup.string().optional().nullable(),
    email: yup.string().email(t('common:validationMessages.email')).optional().nullable(),
    homesite: yup.string().optional().nullable(),
    postGiro: yup.string().optional().nullable(),
    bankGiro: yup.string().optional().nullable(),
    orgNr: yup.string().optional().nullable(),
    admDate: yup.string().optional().nullable(),
    country: yup.string().optional().nullable(),
    expireDate: yup.string().optional().nullable(),
    gracePeriod: yup.number().min(0).optional().nullable(),
    comment: yup.string().optional().nullable(),
  });

  const formMethods = useForm<OrganizationRequest>({
    resolver: yupResolver(OrganizationRequestSchema),
    mode: 'all',
    defaultValues: {
      countryAss_id: 0,
      countryAssNr: '',
      countryAss: '',
      cOaddress: '',
      address: '',
      postNr: '',
      postAddress: '',
      addressLocal: '',
      phone: '',
      phoneOther: '',
      fax: '',
      email: '',
      homesite: '',
      postGiro: '',
      bankGiro: '',
      orgNr: '',
      admDate: '',
      country: '',
      expireDate: '',
      gracePeriod: 0,
      comment: '',
    },
  });

  const { handleSubmit, reset, watch } = formMethods;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAllOrganizations();
      ConsoleHelper.log('OrganizationDetails fetchData result: ', result);
      if (isMounted()) {
        reset(result.data.results[0]);
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted, reset]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSubmit = async (data: OrganizationRequest) => {
    const dataToPost: OrganizationRequest = {
      ...data,
      admDate: DateHelper.getFSDate(data.admDate),
    };
    setLoading(true);
    try {
      await updateorganization(dataToPost);
      if (isMounted()) {
        toast.success(<>{t('organization:organizationPage.saved')}</>);
        fetchData();
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  const fieldLabel = (field: string) => {
    return t(`organization:labelFields.${field}`);
  };

  return (
    <FormLoaderWrapper loading={loading}>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ p: 3 }}>
            <LoadingOverlay loading={loading} />
            <Grid spacing={3} container>
              <Grid item xs={12} lg={7}>
                <Typography variant='body1' sx={{ fontWeight: 600, mb: 2 }}>
                  {watch('countryAss')}
                </Typography>
                <Grid spacing={2} container>
                  <Grid item xs={12} sm={4}>
                    <ControlledTextField disabled label={fieldLabel('countryAssNr')} name='countryAssNr' />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <ControlledTextField required label={fieldLabel('countryAss')} name='countryAss' />
                  </Grid>
                </Grid>

                <Typography variant='body1' sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
                  {t('organization:organizationPage.other')}
                </Typography>
                <Grid spacing={2} container>
                  <Grid item xs={12} md={8}>
                    <ControlledTextField label={fieldLabel('country')} name='country' />
                  </Grid>
                  <Grid item xs={12} md={4} />
                  <Grid item xs={6} md={4}>
                    <ControlledTextField label={fieldLabel('postGiro')} name='postGiro' />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <ControlledTextField label={fieldLabel('bankGiro')} name='bankGiro' />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <ControlledTextField label={fieldLabel('orgNr')} name='orgNr' />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <ControlledDatePicker label={fieldLabel('admDate')} name='admDate' />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <ControlledTextField label={fieldLabel('expireDate')} name='expireDate' />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <ControlledTextField type='number' label={fieldLabel('gracePeriod')} name='gracePeriod' />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledTextField label={fieldLabel('comment')} multiline rows={4} name='comment' />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} lg={5}>
                <Typography variant='body1' sx={{ fontWeight: 600, mb: 2 }}>
                  {t('organization:organizationPage.contact')}
                </Typography>
                <Grid spacing={2} container>
                  <Grid item xs={6}>
                    <ControlledTextField label={fieldLabel('address')} name='address' />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField label={fieldLabel('COaddress')} name='cOaddress' />
                  </Grid>
                  <Grid item xs={4}>
                    <ControlledTextField label={fieldLabel('postNr')} name='postNr' />
                  </Grid>
                  <Grid item xs={8}>
                    <ControlledTextField label={fieldLabel('postAddress')} name='postAddress' />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledTextField label={fieldLabel('addressLocal')} name='addressLocal' />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledTextField label={fieldLabel('fax')} name='fax' />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledTextField label={fieldLabel('email')} name='email' />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField label={fieldLabel('phone')} name='phone' />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField label={fieldLabel('phoneOther')} name='phoneOther' />
                  </Grid>
                </Grid>
                <Grid item xs={12} mt={3.5}>
                  <ControlledTextField label={fieldLabel('homesite')} name='homesite' />
                </Grid>
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button type='submit' variant='contained' disableElevation disabled={loading}>
                  {t('common:button.save')}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </form>
      </FormProvider>
    </FormLoaderWrapper>
  );
};
